import { useNavigate, useParams, Link } from "react-router-dom";
import {
  getFilmById,
  getActorsByIds,
  getNewsForFilmCast,
  getRelatedFilms,
  typeLabels,
} from "@/utils/catalog";
import FilmCard from "@/components/base/FilmCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FavoriteButton from "@/components/base/FavoriteButton";
import ShareButton from "@/components/base/ShareButton";
import RelatedNews from "@/pages/detail/components/RelatedNews";
import ActorMiniCard from "@/pages/detail/components/ActorMiniCard";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function FilmDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const film = id ? getFilmById(id) : undefined;
  const { isFilmFavorite, toggleFilm } = useFavorites();

  useSeo(
    film ? `${film.title}详情介绍 - 葵司` : "页面未找到 | 葵司影视资讯",
    film
      ? `《${film.title}》是${film.year}年上映的${typeLabels[film.type]}，由${film.director}执导，${getActorsByIds(film.cast)
          .map((a) => a.name)
          .join("、")}主演。${film.synopsis}`
      : "抱歉，未找到对应的影视作品。",
    film
      ? `葵司,${film.title},${typeLabels[film.type]},${film.tags.join(",")},${film.director}`
      : "葵司,影视资讯",
  );

  if (!film) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-20">
        <EmptyState
          icon="ri-film-line"
          title="未找到该影视作品"
          description="该作品可能已下线或链接有误，返回分类页继续浏览吧。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const cast = getActorsByIds(film.cast);
  const relatedNews = getNewsForFilmCast(film);
  const relatedFilms = getRelatedFilms(film, 4);
  const saved = isFilmFavorite(film.id);

  const infoRows = [
    { label: "类型", value: typeLabels[film.type] },
    { label: "导演", value: film.director },
    { label: "上映时间", value: `${film.year}年` },
    { label: "地区", value: film.region },
    { label: "评分", value: `${film.rating.toFixed(1)} 分` },
    { label: "题材标签", value: film.tags.join(" / ") },
  ];

  return (
    <div>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: typeLabels[film.type], to: `/category/${film.type}` },
              { label: film.title },
            ]}
          />
        </div>
      </div>

      <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-10">
          <div className="mx-auto w-full max-w-[280px] lg:max-w-none">
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={film.cover}
                alt={`${film.title}海报`}
                title={`${film.title} - 葵司影视资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/category/${film.type}`}
                className="px-3 h-7 flex items-center rounded-full bg-primary-500 text-background-50 text-xs"
              >
                {typeLabels[film.type]}
              </Link>
              {film.tags.map((t) => (
                <span
                  key={t}
                  className="px-3 h-7 flex items-center rounded-full bg-secondary-100 text-secondary-900 text-xs"
                >
                  {t}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-foreground-950">
              {film.title}
            </h1>
            <p className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-1 text-sm text-foreground-600">
              <span>{film.year}年</span>
              <span className="text-background-300">|</span>
              <span>{film.region}</span>
              <span className="text-background-300">|</span>
              <span>导演 {film.director}</span>
              <span className="text-background-300">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500" />
                {film.rating.toFixed(1)}
              </span>
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => toggleFilm(film.id)}
                className={`h-10 px-5 flex items-center gap-2 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  saved
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={saved ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                {saved ? "已收藏" : "收藏作品"}
              </button>
              <ShareButton title={`《${film.title}》 - 葵司影视资讯`} />
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-bold text-base text-foreground-950">
                基本资料
              </h2>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="shrink-0 text-foreground-500 sm:w-20">
                      {row.label}
                    </dt>
                    <dd className="text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 pt-5 border-t border-background-200">
                <h3 className="font-heading font-bold text-base text-foreground-950">
                  剧情简介
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  {film.synopsis}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-content mx-auto px-4 md:px-6 pb-4">
        <RelatedNews items={relatedNews} title="资讯动态" />
      </div>

      <section className="max-w-content mx-auto px-4 md:px-6 py-10">
        <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950">
          相关推荐
        </h2>
        <p className="mt-2 text-sm text-foreground-500">
          根据作品类型、导演与演员关联为你推荐。
        </p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {relatedFilms.map((f) => (
            <FilmCard key={f.id} film={f} />
          ))}
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-6 pb-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="font-heading font-bold text-lg text-foreground-950">
            主要演员
          </h2>
          <div className="mt-5 flex flex-wrap gap-6">
            {cast.map((a) => (
              <ActorMiniCard key={a.id} actor={a} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}